import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Home, Loader2, MapPin, Navigation, X } from 'lucide-react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { useShopperLocation } from '../../hooks/useShopperLocation';
import { reverseGeocode } from '../../lib/geocode';
import { AddressAutocomplete } from '../ui/AddressAutocomplete';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

interface SavedAddress {
  id: string;
  label: string;
  street: string;
  apt?: string | null;
  city: string;
  state: string;
  zip: string;
  isDefault?: boolean;
  lat?: number;
  lng?: number;
}

interface LocationPickerSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export function LocationPickerSheet({ isOpen, onClose }: LocationPickerSheetProps) {
  const { user } = useAuth();
  const { location, setLocation } = useShopperLocation();

  const [form, setForm] = useState({ street: '', city: '', state: '', zipCode: '' });
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setForm({
      street: location?.street || '',
      city: location?.city || '',
      state: location?.state || '',
      zipCode: location?.zipCode || '',
    });
    setCoords(
      typeof location?.lat === 'number' && typeof location?.lng === 'number'
        ? { lat: location.lat, lng: location.lng }
        : null
    );
  }, [isOpen, location]);

  useEffect(() => {
    if (!isOpen || !user) {
      setSavedAddresses([]);
      return;
    }
    setLoadingSaved(true);
    getDocs(query(collection(db, 'users', user.uid, 'addresses'), orderBy('isDefault', 'desc')))
      .then((snap) => {
        setSavedAddresses(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as SavedAddress));
      })
      .catch(() => setSavedAddresses([]))
      .finally(() => setLoadingSaved(false));
  }, [isOpen, user]);

  useEffect(() => {
    if (!isOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  function handleUseCurrentLocation() {
    if (!navigator.geolocation) {
      setError('Location is not supported on this device.');
      return;
    }
    setLocating(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const point = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        const parsed = await reverseGeocode(point);
        if (parsed) {
          setForm({
            street: parsed.street,
            city: parsed.city,
            state: parsed.state,
            zipCode: parsed.zipCode,
          });
        }
        setCoords(point);
        setLocating(false);
      },
      () => {
        setLocating(false);
        setError('Could not get your location. Check browser permissions.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  async function handleSelectSaved(addr: SavedAddress) {
    setSaving(true);
    await setLocation(
      {
        street: addr.street,
        city: addr.city,
        state: addr.state,
        zipCode: addr.zip,
        lat: addr.lat,
        lng: addr.lng,
        addressId: addr.id,
      },
      'profile'
    );
    setSaving(false);
    onClose();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const hasAddress = form.city.trim() || form.zipCode.trim();
    if (!hasAddress && !coords) {
      setError('Enter an address, or use your current location.');
      return;
    }
    setSaving(true);
    setError(null);
    await setLocation({ ...form, ...(coords || {}) }, coords && !hasAddress ? 'device' : 'manual');
    setSaving(false);
    onClose();
  }

  const sourceNote =
    location?.source === 'ip'
      ? 'We estimated this from your network. Set an address for accurate delivery options.'
      : location?.source === 'profile'
        ? 'Using the default address saved on your profile.'
        : null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center md:justify-center">
      <button
        type="button"
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        aria-label="Close location picker"
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Set delivery location"
        className="relative w-full md:max-w-md bg-white rounded-t-2xl md:rounded-2xl shadow-xl max-h-[88vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3.5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-heading">Delivery location</h2>
            <p className="text-xs text-body">We&apos;ll show boutiques that deliver to you</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-heading" />
          </button>
        </div>

        <div className="px-4 py-4 space-y-4">
          {sourceNote && (
            <div className="flex items-start gap-2 rounded-xl bg-gray-50 px-3 py-2.5">
              <MapPin className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <p className="text-xs text-body">{sourceNote}</p>
            </div>
          )}

          <button
            type="button"
            onClick={handleUseCurrentLocation}
            disabled={locating}
            className="flex items-center gap-2 w-full px-4 py-3 rounded-xl border border-gray-200 text-sm font-medium text-heading hover:bg-gray-50 transition-colors disabled:opacity-60"
          >
            {locating ? (
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            ) : (
              <Navigation className="h-4 w-4 text-primary" />
            )}
            {locating ? 'Finding you...' : 'Use current location'}
          </button>

          {user && (
            <div>
              <p className="text-xs font-medium text-body uppercase tracking-wider mb-2">
                Saved addresses
              </p>
              {loadingSaved ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
              ) : savedAddresses.length === 0 ? (
                <p className="text-sm text-body">
                  No saved addresses.{' '}
                  <Link to="/settings/addresses" className="text-primary hover:underline">
                    Add one
                  </Link>
                </p>
              ) : (
                <div className="space-y-2">
                  {savedAddresses.map((addr) => {
                    const Icon =
                      addr.label === 'Work' ? Briefcase : addr.label === 'Home' ? Home : MapPin;
                    const selected = location?.addressId === addr.id;
                    return (
                      <button
                        key={addr.id}
                        type="button"
                        onClick={() => handleSelectSaved(addr)}
                        disabled={saving}
                        className={cn(
                          'w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-colors disabled:opacity-60',
                          selected
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 hover:bg-gray-50'
                        )}
                      >
                        <Icon className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-heading">{addr.label}</p>
                          <p className="text-xs text-body truncate">
                            {addr.street}
                            {addr.apt ? `, ${addr.apt}` : ''}, {addr.city}, {addr.state} {addr.zip}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-heading mb-1">Street address</label>
              <AddressAutocomplete
                value={form.street}
                onChange={(val) => {
                  setForm((f) => ({ ...f, street: val }));
                  setCoords(null);
                }}
                onSelect={(parsed) => {
                  setForm({
                    street: parsed.street,
                    city: parsed.city,
                    state: parsed.state,
                    zipCode: parsed.zipCode,
                  });
                  setCoords(
                    typeof parsed.lat === 'number' && typeof parsed.lng === 'number'
                      ? { lat: parsed.lat, lng: parsed.lng }
                      : null
                  );
                }}
                placeholder="Search for an address..."
              />
            </div>

            <div className="grid grid-cols-6 gap-2">
              <div className="col-span-3">
                <label className="block text-xs font-medium text-heading mb-1">City</label>
                <Input
                  value={form.city}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, city: e.target.value }));
                    setCoords(null);
                  }}
                  placeholder="New York"
                />
              </div>
              <div className="col-span-1">
                <label className="block text-xs font-medium text-heading mb-1">State</label>
                <Input
                  value={form.state}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, state: e.target.value.toUpperCase() }));
                    setCoords(null);
                  }}
                  placeholder="NY"
                  maxLength={2}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-heading mb-1">ZIP</label>
                <Input
                  value={form.zipCode}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, zipCode: e.target.value }));
                    setCoords(null);
                  }}
                  placeholder="10012"
                  maxLength={10}
                />
              </div>
            </div>

            {error && <p className="text-xs text-red-600">{error}</p>}

            <Button type="submit" className="w-full" disabled={saving}>
              {saving ? 'Saving...' : 'Use this location'}
            </Button>
          </form>

          {!user && (
            <p className="text-xs text-center text-body">
              <Link to="/signin" className="text-primary hover:underline">
                Sign in
              </Link>{' '}
              to use saved addresses and check out faster.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
